import * as React from "react";
import { useState, useMemo } from 'react';
import { 
    AutocompleteArrayInput, 
    DateInput, 
    ReferenceArrayInput, 
    SimpleForm, 
    required, 
    useNotify,
    Loading,
    useTranslate,
    useDataProvider,
    useLocaleState,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { 
    Box, Card, CardContent, Grid, Button, Typography, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Tooltip, CircularProgress, List, ListItem, ListItemText, Divider, 
    Popper, Paper, ClickAwayListener, GlobalStyles 
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableViewIcon from '@mui/icons-material/TableView';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import EventIcon from '@mui/icons-material/Event';

import { useUserPreferences } from "../hooks/useUserPreferences";

interface ReportRow {
    employee_fullname: string;
    type: string;
    dtstart: string;
    dtend?: string;
    duration: number;
    title?: string;
    symbol?: string; // <--- NUOVO CAMPO
}

interface CellData {
    total: number;
    events: ReportRow[];
}

const COL_EMP_WIDTH = 120;
const COL_TYPE_WIDTH = 50; 
const COL_DATE_WIDTH = 60;

const TYPE_PRIORITY: Record<string, number> = {
    'JOB': 1, 'OVERTIME': 2, 'HOLIDAY': 3, 'PERMIT': 4, 'ILLNESS': 5
};

// Se vuoi colorare diversamente le lettere
const SYMBOL_MAPPING: Record<string, string> = {
    'ILLNESS': 'M', 'PERMIT': 'P'
};

const scrollbarStyles = {
    '*::-webkit-scrollbar': { width: '14px', height: '14px' },
    '*::-webkit-scrollbar-track': { backgroundColor: '#f5f5f5', borderLeft: '1px solid #e0e0e0' },
    '*::-webkit-scrollbar-thumb': { backgroundColor: '#b0bec5', borderRadius: '8px', border: '3px solid #f5f5f5' },
    '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#78909c' },
};

const formatDateLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const SelectAllEmployeesButton = () => {
    const dataProvider = useDataProvider();
    const { setValue } = useFormContext();
    const translate = useTranslate();
    const [loading, setLoading] = useState(false);

    const handleSelectAll = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('employee', {
                pagination: { page: 1, perPage: 1000 }, 
                sort: { field: 'fullname', order: 'ASC' },
                filter: {}
            });
            const allIds = data.map((record: any) => record.id);
            setValue('employee', allIds, { shouldValidate: true });
        } catch (error) { console.error(error); } 
        finally { setLoading(false); }
    };

    return (
        <Button onClick={handleSelectAll} disabled={loading} size="small" startIcon={loading ? <CircularProgress size={16} /> : <PlaylistAddCheckIcon />} sx={{ mb: 1, textTransform: 'none' }}>
            {translate('ess.calendar.report.select_all_employees', { _: 'Seleziona tutti i dipendenti' })}
        </Button>
    );
};

export default function EventMatrixReport() {
    const translate = useTranslate();
    const notify = useNotify();
    const [locale] = useLocaleState();
    const { userPreferences, upLoading } = useUserPreferences();

    const [reportData, setReportData] = useState<ReportRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [lastFilters, setLastFilters] = useState<any>(null);

    const [anchorEl, setAnchorEl] = useState<HTMLTableCellElement | null>(null);
    const [popoverContent, setPopoverContent] = useState<ReportRow[]>([]);
    const [popoverTitle, setPopoverTitle] = useState<string>("");
    const [selectedCellId, setSelectedCellId] = useState<string | null>(null);

    const todayStr = useMemo(() => formatDateLocal(new Date()), []);

    const { defaultStart, defaultEnd } = useMemo(() => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { defaultStart: formatDateLocal(first), defaultEnd: formatDateLocal(last) };
    }, []);

    const handlePreview = async (data: any) => {
        setLoading(true);
        try {
            const timezone = userPreferences?.timezone || 'Europe/Rome';
            const startZoned = new Date(`${data.start}T00:00:00Z`);
            const endZoned = new Date(`${data.end}T23:59:59Z`);
            const employeesStr = data.employee ? data.employee.join(',') : '';
            const url = `../api/reports/excel/event-matrix-data?start=${encodeURIComponent(startZoned.toISOString())}&end=${encodeURIComponent(endZoned.toISOString())}&employees=${employeesStr}&timezone=${timezone}`;
            const response = await fetch(url, { method: 'GET', credentials: 'include' });
            if (!response.ok) throw new Error("Errore nel recupero dati");
            const json = await response.json();
            setReportData(json);
            setLastFilters({ start: startZoned, end: endZoned, employees: employeesStr, timezone });
        } catch (error) { notify(translate('ess.calendar.report.error_preview'), { type: 'error' }); } 
        finally { setLoading(false); }
    };

    const handleDownloadExcel = async () => {
        if (!lastFilters) return;
        try {
            const { start, end, employees, timezone } = lastFilters;
            const url = `../api/reports/excel/event-matrix?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}&employees=${employees}&timezone=${timezone}`;
            const response = await fetch(url, { method: 'GET', credentials: 'include' });
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Report_Ore_${formatDateLocal(start)}_${formatDateLocal(end)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) { notify(translate('ess.calendar.report.error_download'), { type: 'error' }); }
    };

    const handleCellClick = (event: React.MouseEvent<HTMLTableCellElement>, cellData: CellData | undefined, dateStr: string, cellId: string) => {
        if (!cellData || !cellData.events || cellData.events.length === 0) return;
        // Filtriamo gli eventi finti (padding)
        const realEvents = cellData.events.filter(e => e.duration > 0 || e.symbol);
        if (realEvents.length === 0) return;

        setAnchorEl(event.currentTarget);
        setPopoverContent(realEvents); // Mostriamo solo eventi reali nel popover
        setSelectedCellId(cellId);
        const dateObj = new Date(dateStr);
        const title = dateObj.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
        setPopoverTitle(title.charAt(0).toUpperCase() + title.slice(1));
    };

    const handlePopoverClose = () => { setAnchorEl(null); setPopoverContent([]); setSelectedCellId(null); };

    const { uniqueDates, groupedData } = useMemo(() => {
        if (!reportData || reportData.length === 0) return { uniqueDates: [], groupedData: {} };
        const datesSet = new Set<string>();
        const groups: any = {};
        reportData.forEach(row => {
            const dateKey = row.dtstart ? row.dtstart.substring(0, 10) : 'N/A';
            datesSet.add(dateKey);
            if (!groups[row.employee_fullname]) groups[row.employee_fullname] = {};
            if (!groups[row.employee_fullname][row.type]) groups[row.employee_fullname][row.type] = {};
            if (!groups[row.employee_fullname][row.type][dateKey]) groups[row.employee_fullname][row.type][dateKey] = { total: 0, events: [] };
            
            const cell = groups[row.employee_fullname][row.type][dateKey];
            cell.total += row.duration; // La durata sarà 0 per Malattia, ma va bene così non somma
            cell.events.push(row);
        });
        return { uniqueDates: Array.from(datesSet).sort(), groupedData: groups };
    }, [reportData]);

    const resultsTitle = useMemo(() => {
        if (!lastFilters) return translate('ess.calendar.report.results');
        return `${translate('ess.calendar.report.summary_hours')} ${translate('ess.calendar.common.from')} ${lastFilters.start.toLocaleDateString(locale)} ${translate('ess.calendar.common.to')} ${lastFilters.end.toLocaleDateString(locale)}`;
    }, [lastFilters, locale, translate]);

    if (upLoading) return <Loading />;

    return (
        <Grid container spacing={2} sx={{ p: 2, height: '100%' }}>
            <GlobalStyles styles={scrollbarStyles} />
            {/* FILTRI (Uguale a prima) */}
            <Grid size={{ xs: 12, md: 3 }}>
                <Card sx={{ height: "85vh", display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #eee', bgcolor: '#fafafa', flexShrink: 0 }}>
                        <Typography variant="h6" color="primary">{translate('ess.calendar.report.parameters')}</Typography>
                    </Box>
                    <CardContent className="custom-scrollbar" sx={{ flex: 1, overflowY: 'auto', pt: 2 }}>
                        <SimpleForm onSubmit={handlePreview} toolbar={false} defaultValues={{ start: defaultStart, end: defaultEnd }}>
                            <Box display="flex" flexDirection="column" gap={2} width="100%">
                                <DateInput source="start" label={translate('ess.calendar.report.start_date')} validate={required()} fullWidth />
                                <DateInput source="end" label={translate('ess.calendar.report.end_date')} validate={required()} fullWidth />
                                <Box>
                                    <SelectAllEmployeesButton />
                                    <ReferenceArrayInput source="employee" reference="employee" validate={required()} sort={{ field: 'fullname', order: 'ASC' }}>
                                        <AutocompleteArrayInput label={translate('ess.calendar.report.employee')}  optionText="fullname" filterToQuery={s => ({ fullname: { ilike: `${s}` } })} fullWidth helperText={translate('ess.calendar.report.required_field', { _: 'Seleziona almeno un dipendente' })} />
                                    </ReferenceArrayInput>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Button type="submit" variant="contained" size="large" startIcon={loading ? <CircularProgress size={20} color="inherit"/> : <TableViewIcon />} disabled={loading} fullWidth>{translate('ess.calendar.report.preview')}</Button>
                                <Button variant="outlined" color="inherit" fullWidth onClick={() => window.location.reload()} sx={{ mt: 1 }}>Reset</Button>
                            </Box>
                        </SimpleForm>
                    </CardContent>
                </Card>
            </Grid>

            {/* TABELLA */}
            <Grid size={{ xs: 12, md: 9 }}>
                <Card sx={{ height: "85vh", display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #eee', bgcolor: '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <Typography variant="h6">{resultsTitle}</Typography>
                        <Button variant="contained" color="success" startIcon={<FileDownloadIcon />} onClick={handleDownloadExcel} disabled={!lastFilters || loading}>{translate('ess.calendar.report.download_excel')}</Button>
                    </Box>
                    <CardContent sx={{ flex: 1, overflow: 'hidden', p: 0, display: 'flex', flexDirection: 'column' }}>
                        {reportData.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={2}>
                                <TableViewIcon sx={{ fontSize: 80, color: '#ccc' }} /><Typography color="textSecondary">{translate('ess.calendar.report.select_params')}</Typography>
                            </Box>
                        ) : (
                            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ position: 'sticky', left: 0, width: COL_EMP_WIDTH, minWidth: COL_EMP_WIDTH, bgcolor: '#fff', zIndex: 11, fontWeight: 'bold', borderRight: '1px solid #d0d0d0', textAlign: 'center' }}>{translate('ess.calendar.report.employee')}</TableCell>
                                            <TableCell sx={{ position: 'sticky', left: COL_EMP_WIDTH, width: COL_TYPE_WIDTH, minWidth: COL_TYPE_WIDTH, bgcolor: '#fff', zIndex: 11, fontWeight: 'bold', borderRight: '1px solid #d0d0d0', textAlign: 'center' }}>{translate('ess.calendar.report.type')}</TableCell>
                                            {uniqueDates.map(date => {
                                                const dObj = new Date(date);
                                                const isWeekend = dObj.getDay() === 0 || dObj.getDay() === 6;
                                                const isToday = date === todayStr;
                                                return (
                                                    <TableCell key={date} align="center" sx={{ fontWeight: 'bold', width: COL_DATE_WIDTH, minWidth: COL_DATE_WIDTH, color: isToday ? '#1976d2' : (isWeekend ? '#d32f2f' : 'inherit'), bgcolor: isToday ? '#e3f2fd' : '#f5f5f5', padding: '6px 4px', borderRight: '1px solid #e0e0e0', borderBottom: isToday ? '2px solid #1976d2' : 'inherit' }}>{dObj.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })}</TableCell>
                                                );
                                            })}
                                            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#e0e0e0', width: 80, minWidth: 80, zIndex: 10 }}>TOT</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.keys(groupedData).map((empName) => {
                                            const empTypes = Object.keys(groupedData[empName]).sort((a, b) => (TYPE_PRIORITY[a] || 99) - (TYPE_PRIORITY[b] || 99));
                                            
                                            // Calcolo totali giornalieri (solo se non è un simbolo)
                                            const dailyTotals: Record<string, number> = {};
                                            let grandTotalEmp = 0;
                                            uniqueDates.forEach(d => {
                                                dailyTotals[d] = 0;
                                                empTypes.forEach(t => {
                                                    // Se NON è mappato in SYMBOL_MAPPING, lo sommiamo
                                                    if (!SYMBOL_MAPPING[t]) dailyTotals[d] += (groupedData[empName][t][d]?.total || 0);
                                                });
                                                grandTotalEmp += dailyTotals[d];
                                            });

                                            return empTypes.map((typeName, idx) => {
                                                const isSymbolType = !!SYMBOL_MAPPING[typeName];
                                                // Totale Riga: se è simbolo mostriamo '-', altrimenti somma
                                                const rowTotal = isSymbolType ? 0 : uniqueDates.reduce((sum, d) => sum + (groupedData[empName][typeName][d]?.total || 0), 0);

                                                return (
                                                    <TableRow key={`${empName}-${typeName}`} hover>
                                                        {idx === 0 && <TableCell rowSpan={empTypes.length + 1} sx={{ position: 'sticky', left: 0, width: COL_EMP_WIDTH, minWidth: COL_EMP_WIDTH, bgcolor: '#fff', zIndex: 5, borderRight: '1px solid #d0d0d0', borderBottom: '1px solid #ddd', fontWeight: 'bold', textAlign: 'center', lineHeight: 1.2 }}>{empName}</TableCell>}
                                                        <TableCell sx={{ position: 'sticky', left: COL_EMP_WIDTH, width: COL_TYPE_WIDTH, minWidth: COL_TYPE_WIDTH, bgcolor: '#fff', zIndex: 5, borderRight: '1px solid #d0d0d0', textAlign: 'center' }}><Tooltip title={typeName} placement="right"><Typography variant="body2" fontWeight="bold">{translate("ess.calendar.event.type." + typeName?.toLowerCase())?.substring(0,3).toUpperCase()}</Typography></Tooltip></TableCell>
                                                        {uniqueDates.map(date => {
                                                            const cellData = groupedData[empName][typeName][date];
                                                            const dObj = new Date(date);
                                                            const isWeekend = dObj.getDay() === 0 || dObj.getDay() === 6;
                                                            const isToday = date === todayStr;
                                                            const isSelected = selectedCellId === `${empName}-${typeName}-${date}`;

                                                            // --- NUOVA LOGICA: C'è dato se > 0 o se c'è un simbolo ---
                                                            const hasRealData = cellData && cellData.events.some((e: ReportRow) => e.duration > 0 || e.symbol);

                                                            return (
                                                                <TableCell key={date} align="center" 
                                                                    onClick={(e) => hasRealData && handleCellClick(e, cellData, date, `${empName}-${typeName}-${date}`)} 
                                                                    sx={{ 
                                                                        width: COL_DATE_WIDTH, minWidth: COL_DATE_WIDTH, borderRight: '1px solid #e0e0e0',
                                                                        // Colore testo
                                                                        color: hasRealData && cellData.events.some((e: ReportRow) => e.symbol) ? 'secondary.main' : (!hasRealData ? '#e0e0e0' : 'inherit'),
                                                                        // Cursore
                                                                        cursor: hasRealData ? 'pointer' : 'default',
                                                                        // Sfondo
                                                                        backgroundColor: hasRealData ? (isSelected ? '#bbdefb' : (isToday ? '#f1f8ff' : '#transparent')) : (isWeekend ? '#fafafa' : 'transparent'),
                                                                        fontWeight: (isSelected || hasRealData) ? 'bold' : 'normal',
                                                                        '&:hover': { backgroundColor: hasRealData ? (isSelected ? '#90caf9' : '#e3f2fd') : (isWeekend ? '#f0f0f0' : '#f9f9f9') }
                                                                    }}
                                                                >
                                                                    {hasRealData ? (cellData.events.find((e: ReportRow) => e.symbol)?.symbol || cellData.total.toLocaleString(locale)) : '-'}
                                                                </TableCell>
                                                            );
                                                        })}
                                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f9f9f9', width: 80, minWidth: 80 }}>{isSymbolType ? '-' : rowTotal.toLocaleString(locale)}</TableCell>
                                                    </TableRow>
                                                );
                                            }).concat([
                                                <TableRow key={`${empName}-TOT`} sx={{ bgcolor: '#e3f2fd' }}>
                                                    <TableCell sx={{ position: 'sticky', left: COL_EMP_WIDTH, bgcolor: '#bbdefb', zIndex: 5, textAlign: 'center', fontWeight: 'bold', width: COL_TYPE_WIDTH, minWidth: COL_TYPE_WIDTH }}>TOT</TableCell>
                                                    {uniqueDates.map(d => {
                                                        const val = dailyTotals[d];
                                                        const isToday = d === todayStr;
                                                        return <TableCell key={d} align="center" sx={{ fontWeight: 'bold', width: COL_DATE_WIDTH, minWidth: COL_DATE_WIDTH, borderRight: '1px solid #90caf9', bgcolor: isToday ? '#bbdefb' : 'inherit' }}>{val > 0 ? val.toLocaleString(locale) : '-'}</TableCell>;
                                                    })}
                                                    <TableCell align="center" sx={{ fontWeight: '900', bgcolor: '#bbdefb', width: 80, minWidth: 80 }}>{grandTotalEmp.toLocaleString(locale)}</TableCell>
                                                </TableRow>
                                            ]);
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Grid>
            {/* Popper e ClickAwayListener invariati... */}
            <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom" modifiers={[{ name: 'flip', enabled: true }, { name: 'offset', options: { offset: [0, 0] } }]} sx={{ zIndex: 1300 }}>
                <ClickAwayListener onClickAway={handlePopoverClose}>
                    <Paper elevation={6} sx={{ maxHeight: 300, maxWidth: 350, overflowY: 'auto' }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main', textAlign: 'center' }}>{popoverTitle}</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <List dense sx={{ p: 0 }}>
                                {popoverContent.map((evt, i) => (
                                    <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                                        <EventIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                        <ListItemText primary={evt.title || translate("ess.calendar.event.type." + evt.type?.toLowerCase())} secondary={<span>{new Date(evt.dtstart).toLocaleTimeString(locale, {hour:'2-digit', minute:'2-digit'})} <strong>({evt.duration > 0 ? evt.duration + 'h' : (evt.symbol || '')})</strong></span>} primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Grid>
    );
}